import React, { useState, useEffect, useRef } from 'react';
import '../styles/Carousel.scss';

const Carousel = ({ 
  items, 
  type = 'image', // 'image' or 'video'
  autoPlay = false,
  autoPlayInterval = 5000,
  showDots = true,
  showArrows = true,
  showCounter = true,
  itemsPerView = 1,
  onItemClick = null,
  className = ''
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const intervalRef = useRef(null);
  const carouselRef = useRef(null);

  // For simplicity, let's use individual item navigation instead of grouped slides
  const maxIndex = items && items.length > 0 ? items.length - 1 : 0;

  // Auto play functionality
  useEffect(() => {
    if (isPlaying && autoPlay && items.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
      }, autoPlayInterval);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isPlaying, autoPlay, autoPlayInterval, maxIndex, items.length]);

  // Navigation functions
  const goToSlide = (index) => {
    setCurrentIndex(Math.max(0, Math.min(index, maxIndex)));
  };

  const nextSlide = () => {
    setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentIndex(prev => (prev <= 0 ? maxIndex : prev - 1));
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
      } else if (e.key === ' ') {
        e.preventDefault();
        setIsPlaying(prev => !prev);
      }
    };

    const currentRef = carouselRef.current;
    if (currentRef) {
      currentRef.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [nextSlide, prevSlide]);

  // Handle item click
  const handleItemClick = (item, index) => {
    if (onItemClick) {
      onItemClick(item, index);
    }
  };

  // Render carousel item based on type
  const renderItem = (item, index) => {
    if (type === 'video') {
      return (
        <div 
          key={index}
          className="carousel-item video-item"
          onClick={() => handleItemClick(item, index)}
        >
          <div className="video-preview">
            <img
              src={item.thumbnail}
              alt={item.title}
              className="video-thumbnail"
              onError={(e) => {
                e.target.style.backgroundColor = '#333';
                e.target.style.display = 'flex';
                e.target.style.alignItems = 'center';
                e.target.style.justifyContent = 'center';
                e.target.innerHTML = '🎬';
                e.target.style.fontSize = '3rem';
                e.target.style.color = '#666';
              }}
            />
            <div className="play-button">▶</div>
            <div className="video-overlay">
              <h4>{item.title}</h4>
              <p>{item.description}</p>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div 
          key={index}
          className="carousel-item image-item"
          onClick={() => handleItemClick(item, index)}
        >
          <img 
            src={item.src} 
            alt={item.alt}
            onError={(e) => {
              e.target.style.backgroundColor = '#333';
              e.target.style.display = 'flex';
              e.target.style.alignItems = 'center';
              e.target.style.justifyContent = 'center';
              e.target.innerHTML = '🖼️';
              e.target.style.fontSize = '3rem';
              e.target.style.color = '#666';
            }}
          />
          <div className="image-overlay">
            <h4>{item.title}</h4>
            <p>{item.description}</p>
          </div>
        </div>
      );
    }
  };

  // Get visible items for current slide (currently unused but kept for future features)
  // const getVisibleItems = () => {
  //   const startIndex = currentIndex * itemsPerView;
  //   return items.slice(startIndex, startIndex + itemsPerView);
  // };

  // Early return for empty items - after all hooks
  if (!items || items.length === 0) {
    return (
      <div className="carousel-empty">
        <div className="empty-icon">📷</div>
        <div className="empty-message">אין פריטים להצגה</div>
      </div>
    );
  }

  // Calculate how many items to show at once
  const getVisibleItems = () => {
    const visibleItems = [];
    for (let i = 0; i < itemsPerView && i < items.length; i++) {
      const index = (currentIndex + i) % items.length;
      visibleItems.push({ item: items[index], originalIndex: index });
    }
    return visibleItems;
  };

  const visibleItems = getVisibleItems();

  return (
    <div 
      className={`carousel ${className}`}
      ref={carouselRef}
      tabIndex={0}
      role="region"
      aria-label="Content carousel"
    >
      {/* Carousel Container */}
      <div 
        className="carousel-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="carousel-track">
          <div className="carousel-slide">
            <div className="carousel-slide-content">
              {visibleItems.map(({ item, originalIndex }) => 
                renderItem(item, originalIndex)
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      {showArrows && items.length > itemsPerView && (
        <>
          <button
            className="carousel-arrow carousel-arrow-prev"
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            className="carousel-arrow carousel-arrow-next"
            onClick={nextSlide}
            aria-label="Next slide"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {showDots && items.length > itemsPerView && (
        <div className="carousel-dots">
          {items.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to item ${index + 1}`}
            />
          ))}
        </div>
      )}


      {/* Slide Counter */}
      {showCounter && items.length > 1 && (
        <div className="carousel-counter">
          <span>{currentIndex + 1} / {items.length}</span>
        </div>
      )}
    </div>
  );
};

export default Carousel;
